/**
 * Formatting utilities for the dashboard
 */

/**
 * Format duration in seconds to human-readable string
 */
export function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes < 60) {
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  return `${hours}h ${remainingMinutes}m`;
}

/**
 * Format date to localized string
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('de-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Format date and time to localized string
 */
export function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('de-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return 'gerade eben';
  }
  if (diffMinutes < 60) {
    return `vor ${diffMinutes} Minute${diffMinutes === 1 ? '' : 'n'}`;
  }
  if (diffHours < 24) {
    return `vor ${diffHours} Stunde${diffHours === 1 ? '' : 'n'}`;
  }
  if (diffDays < 7) {
    return `vor ${diffDays} Tag${diffDays === 1 ? '' : 'en'}`;
  }

  return formatDate(dateString);
}

/**
 * Format phone number for display
 */
export function formatPhoneNumber(phone: string | null): string {
  if (!phone) return '-';

  // Simple formatting for Swiss numbers
  if (phone.startsWith('+41') && phone.length === 12) {
    return `${phone.slice(0, 3)} ${phone.slice(3, 5)} ${phone.slice(5, 8)} ${phone.slice(8, 10)} ${phone.slice(10)}`;
  }

  return phone;
}

/**
 * Get direction label in German
 */
export function getDirectionLabel(direction: 'inbound' | 'outbound' | null): string {
  switch (direction) {
    case 'inbound':
      return 'Eingehend';
    case 'outbound':
      return 'Ausgehend';
    default:
      return 'Unbekannt';
  }
}

/**
 * Get status label in German
 */
export function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    pending: 'Ausstehend',
    transcribing: 'Transkribiert...',
    evaluating: 'Bewertend...',
    completed: 'Abgeschlossen',
    error: 'Fehler',
    running: 'Laufend',
    paused: 'Pausiert',
    cancelled: 'Abgebrochen',
    failed: 'Fehlgeschlagen',
  };
  return labels[status] || status;
}

/**
 * Get phase label in German
 */
export function getPhaseLabel(phase: string): string {
  const labels: Record<string, string> = {
    erstkontakt: 'Erstkontakt',
    qualifikation: 'Qualifikation',
    angebot: 'Angebot',
    verhandlung: 'Verhandlung',
    abschluss: 'Abschluss',
    nachbearbeitung: 'Nachbearbeitung',
  };
  return labels[phase] || phase;
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}
